import { useEffect, useMemo, useRef, useState } from 'react'

/**
 * Bash-style Ctrl+R reverse history search. Opens a small inline
 * search bar above the prompt, narrows commandHistory as you type,
 * Enter selects (fills the input), Esc cancels. Ctrl+R again scrolls
 * to the next older match.
 *
 * Keyboard contract:
 *   Ctrl+R           open / go to next older match
 *   Ctrl+Shift+R     go to next newer match (if any)
 *   ↑ / ↓            older / newer match
 *   Enter            accept — fill parent input, close
 *   Tab              accept but don't execute (parent usually re-focuses input)
 *   Esc              close without accepting
 */

export function ReverseSearch({
  open,
  history,
  onAccept,
  onClose,
}: {
  open: boolean
  history: string[]
  onAccept: (command: string) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [idx, setIdx] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  // Most-recent-first candidates (dedupe adjacent repeats).
  const recent = useMemo(() => {
    const out: string[] = []
    for (let i = history.length - 1; i >= 0; i--) {
      const cmd = history[i]
      if (out.length && out[out.length - 1] === cmd) continue
      out.push(cmd)
    }
    return out
  }, [history])

  const matches = useMemo(() => {
    if (!query) return recent.slice(0, 40)
    const q = query.toLowerCase()
    return recent.filter(c => c.toLowerCase().includes(q)).slice(0, 40)
  }, [query, recent])

  useEffect(() => {
    if (open) {
      setQuery('')
      setIdx(0)
      setTimeout(() => inputRef.current?.focus(), 0)
    }
  }, [open])

  useEffect(() => {
    setIdx(0)
  }, [query])

  if (!open) return null

  const current = matches[idx]

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault()
      onClose()
      return
    }
    if (e.key === 'Enter') {
      e.preventDefault()
      if (current) onAccept(current)
      onClose()
      return
    }
    if (e.key === 'ArrowDown' || (e.ctrlKey && e.key.toLowerCase() === 'r' && !e.shiftKey)) {
      e.preventDefault()
      setIdx(i => Math.min(i + 1, matches.length - 1))
      return
    }
    if (e.key === 'ArrowUp' || (e.ctrlKey && e.shiftKey && e.key.toLowerCase() === 'r')) {
      e.preventDefault()
      setIdx(i => Math.max(i - 1, 0))
      return
    }
    if (e.key === 'Tab') {
      e.preventDefault()
      if (current) onAccept(current)
      onClose()
    }
  }

  return (
    <div className="px-4 py-2 border-t border-crt-amber/30 bg-black/60 font-mono text-sm">
      <div className="flex items-center gap-3">
        <span className="text-crt-amber shrink-0 text-xs">
          (reverse-i-search)`{query}':
        </span>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKey}
          className="flex-1 bg-transparent outline-none text-crt-bright caret-crt-amber"
          placeholder="type to filter history"
          spellCheck={false}
          autoComplete="off"
          aria-label="Reverse history search"
        />
      </div>
      <div className="mt-1 ml-6 text-crt-green truncate" aria-live="polite">
        {current ?? <span className="text-crt-dim">no match</span>}
      </div>
      {matches.length > 1 && (
        <div className="mt-0.5 ml-6 text-[10px] text-crt-dim">
          {idx + 1} of {matches.length} match{matches.length === 1 ? '' : 'es'} · ↑/↓ or Ctrl+R to cycle · Enter to pick · Esc to cancel
        </div>
      )}
    </div>
  )
}
